import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { companiesAPI, seedAPI, alertsAPI } from '../lib/api';
import CompanyCard from '../components/CompanyCard';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Database, 
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Menu
} from 'lucide-react';

const DashboardPage = () => {
  const { user, isOwner } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyStatuses, setCompanyStatuses] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [companiesRes, alertsRes] = await Promise.all([
        companiesAPI.getAll(),
        alertsAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      setCompanies(companiesRes.data);
      setAlerts(alertsRes.data);

      const statuses = {};
      for (const company of companiesRes.data) {
        try {
          const statusRes = await companiesAPI.getStatus(company.company_id);
          statuses[company.company_id] = statusRes.data;
        } catch (e) {
          console.error(`Error fetching status for ${company.code}:`, e);
        }
      }
      setCompanyStatuses(statuses);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSeed = async () => {
    try {
      setSeeding(true);
      await seedAPI.seed();
      toast.success('Base de datos inicializada con OKRs Q1');
      await fetchData();
    } catch (error) {
      if (error.response?.data?.message?.includes('already seeded')) {
        toast.info('La base de datos ya está inicializada');
      } else {
        toast.error('Error al inicializar datos');
      }
    } finally {
      setSeeding(false);
    }
  };

  const totalKRs = Object.values(companyStatuses).reduce((sum, s) => sum + (s?.total_krs || 0), 0);
  const onTrackKRs = Object.values(companyStatuses).reduce((sum, s) => sum + (s?.on_track_krs || 0), 0);
  const atRiskKRs = Object.values(companyStatuses).reduce((sum, s) => sum + (s?.at_risk_krs || 0), 0);
  const blockedKRs = Object.values(companyStatuses).reduce((sum, s) => sum + (s?.blocked_krs || 0), 0);
  const overallProgress = totalKRs > 0 ? Math.round((onTrackKRs / totalKRs) * 100) : 0;

  const today = new Date().toLocaleDateString('es-ES', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard-page">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-gray-500 text-xs sm:text-sm capitalize">{today}</p>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mt-1">
            Buenos días, {user?.name?.split(' ')[0]} 👋
          </h1>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon"
            className="border-gray-200 hover:bg-gray-100 h-9 w-9 sm:h-10 sm:w-10"
            onClick={fetchData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isOwner && companies.length === 0 && (
            <Button 
              onClick={handleSeed}
              disabled={seeding}
              className="bg-gray-900 hover:bg-gray-800 text-white text-sm"
              data-testid="seed-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{seeding ? 'Inicializando...' : 'Cargar OKRs Q1'}</span>
              <span className="sm:hidden">{seeding ? '...' : 'Cargar'}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Stats Overview - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">Progreso</span>
            <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" />
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{overallProgress}%</p>
          <div className="progress-bar mt-2">
            <div 
              className="progress-bar-indicator"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">On Track</span>
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-emerald-600">{onTrackKRs}</p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">de {totalKRs} KRs</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">At Risk</span>
            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500" />
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-amber-600">{atRiskKRs}</p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1 hidden sm:block">requieren atención</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">Bloqueados</span>
            <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
          </div>
          <p className="text-xl sm:text-2xl font-semibold text-red-600">{blockedKRs}</p>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1 hidden sm:block">acción urgente</p>
        </div>
      </div>

      {/* Alerts - Responsive */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Alertas Activas</span>
            <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full ml-auto">
              {alerts.length}
            </span>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 3).map((alert) => (
              <div 
                key={alert.alert_id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 sm:p-3 bg-white rounded border border-amber-100 gap-2"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">{alert.title}</p>
                  <p className="text-xs text-gray-500 truncate">{alert.message}</p>
                </div>
                <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-900 self-end sm:self-auto">
                  Ver
                </Button>
              </div>
            ))}
          </div>
          {alerts.length > 3 && (
            <p className="text-xs text-amber-700 mt-3 text-center">
              +{alerts.length - 3} alertas más
            </p>
          )}
        </div>
      )}

      {/* Company Cards - Miller's Law: Show max 6 initially, progressive disclosure */}
      <div>
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">
            Empresas
            {companies.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-400">
                ({companies.length})
              </span>
            )}
          </h2>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 rounded-lg h-[160px] sm:h-[180px] animate-pulse" />
            ))}
          </div>
        ) : companies.length === 0 ? (
          /* Fitts's Law: Large, centered CTA button */
          <div className="bg-white border border-gray-200 rounded-lg p-8 sm:p-12 text-center">
            <Database className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              Sin datos inicializados
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Inicializa la base de datos con los OKRs del primer trimestre para comenzar.
            </p>
            {isOwner && (
              <Button 
                onClick={handleSeed} 
                disabled={seeding} 
                className="action-button-primary"
                data-testid="seed-btn-empty"
              >
                <Database className="w-5 h-5 mr-2" />
                {seeding ? 'Inicializando...' : 'Cargar OKRs Q1'}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <CompanyCard 
                key={company.company_id}
                company={company}
                status={companyStatuses[company.company_id]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
